import { useEffect, useState } from 'react';

import Decimal from 'decimal.js';
import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useRecoilValue } from 'recoil';

import { displayNumber } from 'utils/misc';

import { ScenarioFlexCapacitySettingsResponse } from 'RestAPIs/scenario';

import { scenarioAtom } from '../../../atoms';

import { Box, Button, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { useUpdateFlexCapacitySettings } from '../queries';
import {
  GridContainer,
  GridCompact12,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

type Props = {
  data?: ScenarioFlexCapacitySettingsResponse;
};

const FlexCapacityAgreements = ({ data }: Props) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);
  const { year } = useYearQuarterNavigation();

  const [flexPercentage, setFlexPercentage] = useState<number | null>(null);

  useEffect(() => {
    if (typeof data?.flexCapacityPercentage === 'number') {
      setFlexPercentage(
        Decimal.mul(data.flexCapacityPercentage, 100).toNumber()
      );
    }
  }, [data?.flexCapacityPercentage]);

  const hasFlexPercentage = () => typeof flexPercentage === 'number';

  const { mutate: updateScenarioFlexCapacitySettings } =
    useUpdateFlexCapacitySettings();

  const onClickSave = async () => {
    if (!scenario || !data || flexPercentage === null) {
      return;
    }

    try {
      await updateScenarioFlexCapacitySettings({
        scenarioId: scenario.scenarioId,
        year,
        payload: {
          flexCapacityPercentage: Decimal.div(flexPercentage, 100).toNumber()
        }
      });
    } catch {
      setFlexPercentage(
        Decimal.mul(data.flexCapacityPercentage, 100).toNumber()
      ); // Reset to what it was
    }
  };

  return (
    <Box mt={2} mb={4}>
      <GridContainer>
        <GridCompact6>
          <GridContainer display="flex" alignItems="flex-end">
            <GridCompact6>
              <NumericFormat
                customInput={TextField}
                value={flexPercentage ?? ''}
                onValueChange={({ floatValue }) => {
                  if (typeof floatValue === 'number') {
                    setFlexPercentage(floatValue);
                  }
                }}
                suffix="%"
                disabled={!hasFlexPercentage()}
                label={t('formation.agreedFlexPercentage')}
                fullWidth
              />
            </GridCompact6>
            <Grid size={{ compact: 'auto' }}>
              <Button
                variant="outlined"
                onClick={onClickSave}
                disabled={!hasFlexPercentage()}
              >
                {t('save')}
              </Button>
            </Grid>
          </GridContainer>
        </GridCompact6>
        <GridCompact6>
          {data && (
            <Grid container>
              <GridCompact12 textAlign="right">
                <Typography>
                  {t('formation.totalAvailableFlexCapacity')}:{' '}
                  {t('amountHours', {
                    hours: displayNumber(data.flexCapacity)
                  })}
                </Typography>
              </GridCompact12>
              <GridCompact12 textAlign="right">
                <Typography>
                  {t('formation.flexCapacityBalance')}:{' '}
                  {t('amountHours', {
                    hours: displayNumber(data.flexCapacityBalance)
                  })}
                </Typography>
              </GridCompact12>
            </Grid>
          )}
        </GridCompact6>
      </GridContainer>
    </Box>
  );
};

export default FlexCapacityAgreements;
